import { createContext, useContext, useRef } from "react";

/**
 * The presentation an onboarding card is being shown in, handed down by the
 * layer around it rather than read from the flow's atoms - @see
 * `components/OnboardingSurface.tsx` for why.
 */

export type OnboardingSurfaceKind = "modal" | "sheet" | "pill" | "signup";

/**
 * How a step moves on. The main flow's comes from `useOnboardingFlow`; sign-up
 * walks its own shorter sequence and hands its own down instead.
 */
export type OnboardingStepNavigation = {
  /** 1-based, for "Step N of M". */
  stepNumber: number | null;
  totalSteps: number;
  goNext: () => void;
};

type OnboardingSurfaceContextValue = {
  /**
   * `signup` is the card inside the auth modal after an account is created -
   * @see `SignUpOnboarding`.
   */
  presentation: "modal" | "docked" | "signup";
  /** Set by a surface that runs its own sequence. @see `useOnboardingStepNavigation`. */
  navigation?: OnboardingStepNavigation;
  /**
   * The card was mounted by a step change, so its contents slide in. False for
   * a card that is the first one on its surface — the layer is already
   * animating that one in.
   */
  isStepChange: boolean;
};

export const OnboardingSurfaceContext =
  createContext<OnboardingSurfaceContextValue | null>(null);

/** The presentation the enclosing layer is showing. @see the note above. */
export function useOnboardingSurface() {
  const value = useContext(OnboardingSurfaceContext);
  if (!value) {
    throw new Error(
      "useOnboardingSurface must be used inside an onboarding surface layer",
    );
  }
  return value;
}

/**
 * Works out `isStepChange` from successive renders of the flow. A change of
 * surface resets it, so expanding the pill does not slide the card's contents
 * in as if a step had changed.
 */
export function useIsStepChange(
  stepIndex: number,
  surface: OnboardingSurfaceKind | null,
): boolean {
  const previous = useRef({ stepIndex, surface });
  const isStepChange = useRef(false);

  if (previous.current.surface !== surface) {
    isStepChange.current = false;
  } else if (previous.current.stepIndex !== stepIndex) {
    isStepChange.current = true;
  }
  previous.current = { stepIndex, surface };

  return isStepChange.current;
}
